/**
 * Taking action - picks up objects
 *
 * Uses four-phase pattern:
 * 1. validate: Check object can be taken (not scenery, not self, etc.)
 * 2. execute: Transfer the item to actor's inventory
 * 3. report: Generate success events with item and container snapshots
 * 4. blocked: Generate error events when validation fails
 *
 * Supports multi-object commands:
 * - "take all" - takes all portable reachable items
 * - "take all but X" - takes all except specified items
 * - "take X and Y" - takes multiple specified items
 */

import { Action, ActionContext, ValidationResult } from '../../enhanced-types';
import { ActionMetadata } from '../../../validation';
import { ISemanticEvent } from '@sharpee/core';
import { TraitType, SceneryBehavior, ActorBehavior, WearableBehavior, ContainerBehavior, IdentityBehavior, IFEntity, IdentityTrait, getInterceptorForAction, ActionInterceptor, InterceptorSharedData } from '@sharpee/world-model';
import { IFActions } from '../../constants';
import { ScopeLevel } from '../../../scope/types';
import { TakingMessages } from './taking-messages';
import { entityInfoFrom } from '../../../utils';

// Import type guards and typed interfaces
import {
  isWearableTrait,
  hasCapacityLimit,
  getTakingSharedData,
  TakingSharedData,
  TakingItemResult
} from './taking-types';

// Import multi-object helpers
import { isMultiObjectCommand, expandMultiObject } from '../../../helpers/multi-object-handler';

// ============================================================================
// Helper Functions (standalone to avoid `this` issues in object literal)
// ============================================================================

/**
 * Validate taking a single entity
 */
function validateSingleEntity(context: ActionContext, noun: IFEntity): ValidationResult {
  const actor = context.player;

  // Check for interceptor on the target entity (ADR-118)
  // This runs first so entity-specific blocks (e.g., white-hot axe) take priority
  const interceptorResult = getInterceptorForAction(noun, IFActions.TAKING);
  if (interceptorResult) {
    const { interceptor } = interceptorResult;
    const interceptorData: InterceptorSharedData = {};

    // Store interceptor in sharedData for later phases
    const sharedData = getTakingSharedData(context);
    sharedData._interceptor = interceptor;
    sharedData._interceptorData = interceptorData;

    // === PRE-VALIDATE HOOK ===
    if (interceptor.preValidate) {
      const result = interceptor.preValidate(noun, context.world, actor.id, interceptorData);
      if (result !== null && !result.valid) {
        return {
          valid: false,
          error: result.error,
          params: result.params
        };
      }
    }
  }

  // Check scope - must be able to reach the item
  const scopeCheck = context.requireScope(noun, ScopeLevel.REACHABLE);
  if (!scopeCheck.ok) {
    return scopeCheck.error!;
  }

  // Can't take yourself
  if (noun.id === actor.id) {
    return { valid: false, error: TakingMessages.CANT_TAKE_SELF };
  }

  // Check if already holding the item
  const currentLocation = context.world.getLocation(noun.id);
  if (currentLocation === actor.id) {
    return {
      valid: false,
      error: TakingMessages.ALREADY_HAVE,
      params: { item: entityInfoFrom(noun) }
    };
  }

  // Can't take rooms (business rule)
  if (noun.has(TraitType.ROOM)) {
    return {
      valid: false,
      error: TakingMessages.CANT_TAKE_ROOM,
      params: { item: entityInfoFrom(noun) }
    };
  }

  // Can't take scenery (fixed in place)
  if (noun.has(TraitType.SCENERY)) {
    const customMessage = SceneryBehavior.getCantTakeMessage(noun);
    return {
      valid: false,
      error: customMessage || TakingMessages.FIXED_IN_PLACE,
      params: { item: entityInfoFrom(noun) }
    };
  }

  // === POST-VALIDATE HOOK ===
  if (interceptorResult) {
    const interceptor = getTakingSharedData(context)._interceptor;
    const interceptorData = getTakingSharedData(context)._interceptorData ?? {};
    if (interceptor?.postValidate) {
      const result = interceptor.postValidate(noun, context.world, actor.id, interceptorData);
      if (result !== null && !result.valid) {
        return {
          valid: false,
          error: result.error,
          params: result.params
        };
      }
    }
  }

  // Use ActorBehavior to validate capacity constraints
  if (!ActorBehavior.canTakeItem(actor, noun, context.world)) {
    // Check if it's a container capacity issue
    if (actor.has(TraitType.CONTAINER)) {
      const containerTrait = actor.get(TraitType.CONTAINER);
      if (hasCapacityLimit(containerTrait)) {
        // Check item count limit
        if (containerTrait.capacity.maxItems !== undefined) {
          const contents = context.world.getContents(actor.id);
          const currentCount = contents.filter((item: any) => {
            if (!item.has || !item.has(TraitType.WEARABLE)) {
              return true;
            }
            const wearableTrait = item.get(TraitType.WEARABLE);
            return !isWearableTrait(wearableTrait) || !(wearableTrait.isWorn ?? wearableTrait.worn);
          }).length;
          if (currentCount >= containerTrait.capacity.maxItems) {
            return { valid: false, error: TakingMessages.CONTAINER_FULL };
          }
        }

        // Check weight limit
        if (containerTrait.capacity.maxWeight !== undefined) {
          const currentWeight = ContainerBehavior.getTotalWeight(actor, context.world);
          const itemWeight = IdentityBehavior.getWeight(noun);
          if (currentWeight + itemWeight > containerTrait.capacity.maxWeight) {
            return { valid: false, error: TakingMessages.TOO_HEAVY };
          }
        }
      }
    }

    // Otherwise generic failure
    return {
      valid: false,
      error: TakingMessages.CANNOT_TAKE,
      params: { item: entityInfoFrom(noun) }
    };
  }

  return { valid: true };
}

/**
 * Validate a multi-object command (take all, take X and Y)
 */
function validateMultiObject(context: ActionContext): ValidationResult {
  const playerId = context.player.id;
  const items = expandMultiObject(context, {
    scope: 'reachable',
    // Filter out items already carried by the player
    filter: (entity, world) => world.getLocation(entity.id) !== playerId
  });

  if (items.length === 0) {
    return { valid: false, error: TakingMessages.NOTHING_TO_TAKE };
  }

  // Validate each entity, store results
  const results: TakingItemResult[] = items.map(item => {
    const validation = validateSingleEntity(context, item.entity);
    return {
      entity: item.entity,
      success: validation.valid,
      error: validation.valid ? undefined : validation.error
    };
  });

  // Store results for execute/report phases
  const sharedData = getTakingSharedData(context);
  sharedData.multiObjectResults = results;

  // Valid if at least one can be taken
  const anySuccess = results.some(r => r.success);
  if (!anySuccess) {
    // All failed - return the first error
    return { valid: false, error: results[0].error };
  }

  return { valid: true };
}

/**
 * Execute taking a single entity
 */
function executeSingleEntity(
  context: ActionContext,
  noun: IFEntity,
  result: TakingItemResult | TakingSharedData
): void {
  const actor = context.player;

  // Capture context BEFORE any mutations
  const previousLocation = context.world.getLocation(noun.id);
  result.previousLocation = previousLocation;

  // Check if item is worn and needs to be removed first
  if (noun.has(TraitType.WEARABLE)) {
    const wearableTrait = noun.get(TraitType.WEARABLE);
    if (isWearableTrait(wearableTrait) && (wearableTrait.isWorn ?? wearableTrait.worn)) {
      // Mark that we implicitly removed a worn item
      result.implicitlyRemoved = true;
      result.wasWorn = true;

      // Get the wearer (the one who has the item currently)
      const wearer = previousLocation ? context.world.getEntity(previousLocation) : null;
      // Remove the worn status
      if (wearer) {
        WearableBehavior.remove(noun, wearer);
      }
    }
  }

  // Perform the actual move
  context.world.moveEntity(noun.id, actor.id);

  // Score points for taking items with points value (ADR-129)
  const identity = noun.getTrait(IdentityTrait);
  if (identity?.points) {
    context.world.awardScore(
      noun.id,
      identity.points,
      identity.pointsDescription ?? identity.name ?? noun.id
    );
  }
}

/**
 * Generate success events for taking a single entity
 *
 * Uses simplified event pattern (ADR-097): domain event carries messageId directly.
 * Text-service looks up message from domain event - no separate action.success needed.
 */
function reportSingleSuccess(
  context: ActionContext,
  noun: IFEntity,
  result: TakingItemResult | TakingSharedData,
  events: ISemanticEvent[],
  isMultiObject: boolean = false
): void {
  const actor = context.player;

  // Check if we implicitly removed a worn item
  if (result.implicitlyRemoved) {
    const previousLocation = result.previousLocation;
    const container = previousLocation ? context.world.getEntity(previousLocation) : null;

    events.push(context.event('if.event.removed', {
      implicit: true,
      item: noun.name,
      container: container?.name
    }));
  }

  // Determine if taken from a container/supporter
  const previousLocation = result.previousLocation;
  const isFromContainerOrSupporter = previousLocation &&
    previousLocation !== context.world.getLocation(actor.id);

  // Get container name for message
  const containerEntity = previousLocation ? context.world.getEntity(previousLocation) : null;

  // Determine message ID based on context
  let messageKey: string;
  if (isMultiObject) {
    messageKey = TakingMessages.TAKEN_MULTI;
  } else if (isFromContainerOrSupporter) {
    messageKey = TakingMessages.TAKEN_FROM;
  } else {
    messageKey = TakingMessages.TAKEN;
  }

  // Build rendering params: entity-valued keys carry EntityInfo so the
  // formatter chain (`{the:cap:item}`, etc.) can pick the right article.
  // See ADR-158.
  const params = {
    item: entityInfoFrom(noun),
    itemId: noun.id,
    actor: actor.name,
    actorId: actor.id,
    previousLocation: result.previousLocation,
    container: containerEntity ? entityInfoFrom(containerEntity) : ''
  };

  // Emit domain event with messageId (simplified pattern - ADR-097).
  // Top-level fields are domain data (strings) for event-sourcing handlers;
  // params carries EntityInfo for template rendering.
  events.push(context.event('if.event.taken', {
    messageId: `${context.action.id}.${messageKey}`,
    params,
    item: noun.name,
    itemId: noun.id,
    actor: actor.name,
    actorId: actor.id,
    previousLocation: result.previousLocation,
    container: containerEntity?.name || ''
  }));
}

/**
 * Generate blocked event for a single entity that couldn't be taken
 *
 * Uses simplified event pattern (ADR-097): domain event carries messageId directly.
 */
function reportSingleBlocked(
  context: ActionContext,
  noun: IFEntity,
  error: string,
  events: ISemanticEvent[]
): void {
  events.push(context.event('if.event.take_blocked', {
    // Rendering data — EntityInfo for the formatter chain (ADR-158)
    messageId: `${context.action.id}.${error}`,
    params: { item: entityInfoFrom(noun) },
    // Domain data — strings for handlers
    item: noun.name,
    itemId: noun.id,
    reason: error
  }));
}

// ============================================================================
// Action Definition
// ============================================================================

export const takingAction: Action & { metadata: ActionMetadata } = {
  id: IFActions.TAKING,

  // Default scope requirements for this action's slots
  defaultScope: {
    target: ScopeLevel.REACHABLE
  },

  requiredMessages: [
    'no_target',
    'cant_take_self',
    'already_have',
    'cant_take_room',
    'fixed_in_place',
    'container_full',
    'too_heavy',
    'taken',
    'taken_from',
    'nothing_to_take'
  ],

  metadata: {
    requiresDirectObject: true,
    requiresIndirectObject: false,
    directObjectScope: ScopeLevel.REACHABLE
  },

  validate(context: ActionContext): ValidationResult {
    // Check for multi-object command
    if (isMultiObjectCommand(context)) {
      return validateMultiObject(context);
    }

    // Single object validation
    const noun = context.command.directObject?.entity;
    if (!noun) {
      return { valid: false, error: TakingMessages.NO_TARGET };
    }

    return validateSingleEntity(context, noun);
  },

  execute(context: ActionContext): void {
    const sharedData = getTakingSharedData(context);

    // Check for multi-object command
    if (sharedData.multiObjectResults) {
      // Execute for each successful validation
      for (const result of sharedData.multiObjectResults) {
        if (result.success) {
          executeSingleEntity(context, result.entity, result);
        }
      }
      return;
    }

    // Single object execution
    const noun = context.command.directObject!.entity!;
    executeSingleEntity(context, noun, sharedData);
  },

  report(context: ActionContext): ISemanticEvent[] {
    const sharedData = getTakingSharedData(context);
    const events: ISemanticEvent[] = [];

    // Check for multi-object command
    if (sharedData.multiObjectResults) {
      // Generate events for each item (success and failure)
      // Use compact format when multiple items
      const isMultiObject = sharedData.multiObjectResults.length > 1;
      for (const result of sharedData.multiObjectResults) {
        if (result.success) {
          reportSingleSuccess(context, result.entity, result, events, isMultiObject);
        } else {
          reportSingleBlocked(context, result.entity, result.error!, events);
        }
      }
      return events;
    }

    // Single object report
    const noun = context.command.directObject!.entity!;
    reportSingleSuccess(context, noun, sharedData, events, false);
    return events;
  },

  blocked(context: ActionContext, result: ValidationResult): ISemanticEvent[] {
    // blocked() is called when validation fails
    // Uses simplified event pattern (ADR-097)
    const noun = context.command.directObject?.entity;

    // Fully-qualified message IDs (containing dots) are used as-is;
    // short keys (e.g., 'fixed_in_place') get the action ID prefix
    const error = result.error || '';
    const messageId = error.includes('.') ? error : `${context.action.id}.${error}`;

    return [context.event('if.event.take_blocked', {
      // Rendering data — EntityInfo for the formatter chain (ADR-158)
      messageId,
      params: {
        ...result.params,
        item: noun ? entityInfoFrom(noun) : undefined
      },
      // Domain data — strings for handlers
      item: noun?.name,
      itemId: noun?.id,
      reason: result.error
    })];
  },

  group: "object_manipulation"
};
